import { MediaDtoSchema } from "@/modules/common/media/api/mediaDto"
import { UserDtoSchema } from "@/modules/profile/userProfile/userDto"
import { z } from "zod"

/* ── Reaction DTOs ── */

export const MyReactionDtoSchema = z
  .object({
    emoji: z.string(),
  })
  .meta({ id: "MyReactionDtoSchema" })

export const GroupedReactionDtoSchema = z
  .object({
    emoji: z.string(),
    number: z.number(),
  })
  .meta({ id: "GroupedReactionDtoSchema" })

export const ReactionSocialPostDtoSchema = z
  .object({
    myReaction: MyReactionDtoSchema.nullable(),
    allGroupedReactions: z.array(GroupedReactionDtoSchema),
  })
  .meta({ id: "ReactionSocialPostDtoSchema" })

export const ReactionDtoSchema = z
  .object({
    emoji: z.string(),
    user: UserDtoSchema.nullable(),
    creationDate: z.string(),
  })
  .meta({ id: "ReactionDtoSchema" })

/* ── Comment DTOs ── */

export const CommentMentionDtoSchema = z
  .object({
    username: z.string(),
    userId: z.string(),
    startAt: z.number(),
    length: z.number(),
  })
  .meta({ id: "CommentMentionDtoSchema" })

export const CommentDtoSchema: z.ZodType<CommentDto> = z
  .lazy(() =>
    z.object({
      id: z.string(),
      text: z.string(),
      parentCommentId: z.string().nullable(),
      childrenComments: z.array(CommentDtoSchema),
      userDto: UserDtoSchema.nullable(),
      creationDate: z.string(),
      reactions: ReactionSocialPostDtoSchema,
      mentions: z.array(CommentMentionDtoSchema),
      childrenCount: z.number(),
    }),
  )
  .meta({ id: "CommentDtoSchema" })

/* ── Social Post DTOs ── */

export const SocialPostDtoSchema = z
  .object({
    id: z.string(),
    text: z.string().nullable(),
    medias: z.array(MediaDtoSchema),
    creationDate: z.string(),
    reactions: ReactionSocialPostDtoSchema,
    title: z.string().nullable(),
    commentsNumber: z.number(),
  })
  .meta({ id: "SocialPostDtoSchema" })

/* ── Social Resume Light (embedded in session DTO) ── */

export const SocialPostResumeLightDtoSchema = z
  .object({
    id: z.string().nullable(),
    reactions: ReactionSocialPostDtoSchema,
    commentsNumber: z.number(),
  })
  .meta({ id: "SocialPostResumeLightDtoSchema" })

/* ── Input DTOs ── */

export const CreateReactionInputSchema = z
  .object({
    emoji: z.string(),
  })
  .meta({ id: "CreateReactionInputSchema" })

export const CreateCommentInputSchema = z
  .object({
    text: z.string(),
    parentCommentId: z.string().nullable(),
    medias: z.array(MediaDtoSchema),
    mentions: z.array(CommentMentionDtoSchema),
  })
  .meta({ id: "CreateCommentInputSchema" })

/* ── Inferred types ── */

export type MyReactionDto = z.infer<typeof MyReactionDtoSchema>
export type GroupedReactionDto = z.infer<typeof GroupedReactionDtoSchema>
export type ReactionSocialPostDto = z.infer<typeof ReactionSocialPostDtoSchema>
export type ReactionDto = z.infer<typeof ReactionDtoSchema>
export type CommentMentionDto = z.infer<typeof CommentMentionDtoSchema>
export type CommentDto = {
  id: string
  text: string
  parentCommentId: string | null
  childrenComments: CommentDto[]
  userDto: z.infer<typeof UserDtoSchema> | null
  creationDate: string
  reactions: ReactionSocialPostDto
  mentions: CommentMentionDto[]
  childrenCount: number
}
export type MediaDto = z.infer<typeof MediaDtoSchema>
export type SocialPostDto = z.infer<typeof SocialPostDtoSchema>
export type SocialPostResumeLightDto = z.infer<typeof SocialPostResumeLightDtoSchema>
export type CreateReactionInput = z.infer<typeof CreateReactionInputSchema>
export type CreateCommentInput = z.infer<typeof CreateCommentInputSchema>
